import React, { ReactNode } from 'react';
import Container from '@/components/ui/Container';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import MobilePageWrapper from '@/components/ui/MobilePageWrapper';
import SidebarLayout from '@/components/layout/SidebarLayout';

interface UniversalEmptyStateWrapperProps {
  // Data state props
  isLoading: boolean;
  hasData: boolean;
  
  // Header configuration
  title: string;
  description: string;
  icon: ReactNode;
  
  // Layout components
  sidebar?: ReactNode;
  children: ReactNode; // Normal content when data exists
  emptyStateContent: ReactNode; // Centered empty state content
  
  // Additional customization
  className?: string;
}

/**
 * Universal wrapper that handles empty state logic for all generators
 * Eliminates code duplication and provides consistent UX patterns
 */
export const UniversalEmptyStateWrapper: React.FC<UniversalEmptyStateWrapperProps> = ({
  isLoading,
  hasData,
  title,
  description,
  icon,
  sidebar,
  children,
  emptyStateContent,
  className
}) => {
  // Determine layout based on data state
  const shouldShowCenteredLayout = !isLoading && !hasData;

  return (
    <MobilePageWrapper>
      <Container className={className}>
        {/* Conditionally show header only when not in centered layout */}
        {!shouldShowCenteredLayout && (
          <DashboardHeader
            title={title}
            description={description}
            icon={icon}
          />
        )}
        
        <div className="space-y-3 md:space-y-6">
          {shouldShowCenteredLayout ? (
            // Centered empty state layout
            emptyStateContent
          ) : (
            // Normal layout with optional sidebar
            sidebar ? (
              <SidebarLayout sidebar={sidebar}>
                {children}
              </SidebarLayout>
            ) : (
              children
            )
          )}
        </div>
      </Container>
    </MobilePageWrapper>
  );
}; 